/**
 * Customer-impact classification for a bulk PON clear.
 *
 * Extracted from the Provisioning tab for the same reason
 * ``signalTones.ts`` and ``opticalHealth.ts`` exist: the mapping from
 * raw device/portal data to a coloured verdict is the part that must
 * never drift, so it lives on its own and is pinned by tests.
 *
 * The rule this file exists to enforce: **absence of evidence is not
 * evidence of absence.** An ONU we never collected a MAC for looks
 * exactly like an empty one in the inventory table. Folding those two
 * into "no customer" is how a bulk clear takes live subscribers down,
 * so ``unknown`` is a first-class state and always renders as such.
 */

import type { BadgeTone } from "../components/Badge";

/** Service context as returned by the bulk-clear preview endpoint.
 *  Every field is optional: a pre-v0.35 server omits them, and a
 *  preview cached in React state can predate a backend deploy. */
export type ClearImpactInput = {
  vnets?: string[];
  gigafy_devices?: string[];
  live_sessions?: number;
  mac_seen_at?: string | null;
  /** Gigafy portal's moved-in flag for the resolved device. */
  occupied?: boolean | null;
};

/** Ordered worst-first; ``impactOf`` returns the first that matches. */
export type Impact = "live" | "provisioned" | "unknown" | "none";

export function impactOf(o: ClearImpactInput): Impact {
  if ((o.live_sessions ?? 0) > 0) return "live";
  if ((o.gigafy_devices?.length ?? 0) > 0) return "provisioned";
  // Positive evidence of a customer outranks "we have no MAC".
  if (o.occupied === true) return "provisioned";
  // A NULL timestamp and a missing field mean the same thing: we have
  // no evidence either way. Fail toward caution.
  if (!o.mac_seen_at) return "unknown";
  return "none";
}

export const IMPACT_TONE: Record<Impact, BadgeTone> = {
  live: "danger",
  provisioned: "warning",
  unknown: "muted",
  none: "neutral",
};

export const IMPACT_LABEL: Record<Impact, string> = {
  live: "live session",
  provisioned: "customer",
  unknown: "unknown",
  none: "no service seen",
};

/** ISO timestamp → "18d ago". Dateless input renders as an em dash. */
export function agoLabel(iso: string | null | undefined): string {
  if (!iso) return "—";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  return `${days}d ago`;
}
